import {
  resolveGraphSlotChartHeight,
  resolveGraphSlotContainerHeight,
} from './graphSlotLayout';
import { GraphData } from './schemas';

/** 推移グラフ（予定進捗推移）で同時表示できるグラフ項目の最大数 */
export const DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS = 5;

/** 予定進捗推移の複数グラフで recharts syncId に渡す固定値 */
export const DATA_PLAN_PROGRESS_GRAPH_SYNC_ID = 'planProgressGraph';

/** 1本表示時のグラフ高さ（px） */
export const DATA_PLAN_PROGRESS_GRAPH_CHART_HEIGHT = 360;

/** 2本表示時の1本あたりのグラフ高さ（px） */
export const DATA_PLAN_PROGRESS_GRAPH_COMPACT_CHART_HEIGHT = 200;

/** 3本表示時の1本あたりのグラフ高さ（px） */
export const DATA_PLAN_PROGRESS_GRAPH_DENSE_CHART_HEIGHT = 140;

/** 4本以上表示時の1本あたりのグラフ高さ（px） */
export const DATA_PLAN_PROGRESS_GRAPH_OVERFLOW_CHART_HEIGHT = 120;

/**
 * 表示本数（スロット数）に応じたグラフ高さ（px）を返す。
 */
export const resolveDataPlanProgressGraphChartHeight = (
  graphCount: number
): number =>
  resolveGraphSlotChartHeight(graphCount, {
    single: DATA_PLAN_PROGRESS_GRAPH_CHART_HEIGHT,
    double: DATA_PLAN_PROGRESS_GRAPH_COMPACT_CHART_HEIGHT,
    triple: DATA_PLAN_PROGRESS_GRAPH_DENSE_CHART_HEIGHT,
    overflow: DATA_PLAN_PROGRESS_GRAPH_OVERFLOW_CHART_HEIGHT,
  });

/** タイトル・表示設定・枠線など、グラフ本体以外の高さ（px） */
const DATA_PLAN_PROGRESS_GRAPH_CONTAINER_CHROME_HEIGHT = 48;
const DATA_PLAN_PROGRESS_GRAPH_CONTAINER_TITLE_HEIGHT = 40;
const DATA_PLAN_PROGRESS_GRAPH_CONTAINER_CONTROLS_HEIGHT = 72;
const DATA_PLAN_PROGRESS_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT = 56;
const DATA_PLAN_PROGRESS_GRAPH_CONTAINER_SLOT_GAP = 16;
const DATA_PLAN_PROGRESS_GRAPH_CONTAINER_FOOTER_HEIGHT = 52;

/**
 * 複数スロット表示時に ResizableWrapper が収めるべき高さ（px）を返す。
 */
export const resolveDataPlanProgressGraphContainerHeight = (
  slotCount: number
): number =>
  resolveGraphSlotContainerHeight(slotCount, {
    chromeHeight: DATA_PLAN_PROGRESS_GRAPH_CONTAINER_CHROME_HEIGHT,
    titleHeight: DATA_PLAN_PROGRESS_GRAPH_CONTAINER_TITLE_HEIGHT,
    controlsHeight: DATA_PLAN_PROGRESS_GRAPH_CONTAINER_CONTROLS_HEIGHT,
    slotControlsHeight: DATA_PLAN_PROGRESS_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT,
    slotGap: DATA_PLAN_PROGRESS_GRAPH_CONTAINER_SLOT_GAP,
    footerHeight: DATA_PLAN_PROGRESS_GRAPH_CONTAINER_FOOTER_HEIGHT,
    resolveChartHeight: resolveDataPlanProgressGraphChartHeight,
  });

/**
 * 種別全体では選択不可な出玉率。種別全体へ切替時は粗利合計へフォールバックする。
 */
export const PLAN_PROGRESS_GRAPH_DTMART_FIELD = 'dtmart';

/**
 * 種別全体切替時の出玉率フォールバック先（粗利合計）。
 */
export const PLAN_PROGRESS_GRAPH_FALLBACK_FIELD_FOR_ALL_CATEGORY =
  'arariGkForHoseiAndGenkin';

export type PlanProgressGraphFieldItem = {
  field: string;
  data: GraphData;
};

/**
 * 推移グラフ検索条件の型
 */
export type PlanProgressGraphParams = {
  /**
   * 項目コード（複数グラフ表示以前のお気に入り互換用）
   */
  field?: string;
  /**
   * 画面に出すグラフ項目の一覧（最大 DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS 件）
   */
  fields?: string[];
  /**
   * エリアID一覧(未指定で全エリア)
   */
  areas?: string[];
  /**
   * 対象店舗コード(未指定で全店舗)
   */
  halls?: string[];
  /**
   * 表示日付範囲に当日を除外するかどうか(デフォルト:true)
   */
  excludeToday?: boolean;
  /**
   *期間指定（年）
   */
  year?: number;
  /**
   *期間指定（月）
   */
  month?: number;
  /**
   * 種別グループID(未指定で全種別グループ)
   */
  shuGroupIds?: string[];
  /**
   * 種別一覧(未指定で全種別)
   */
  shuList?: string[];
};

/**
 * Redux に保持する検索条件（期間・店舗・種別など）。
 * どのグラフ項目を表示するかは graphList[].field に持ち、setting には含めない。
 */
export type PlanProgressGraphSetting = Omit<
  PlanProgressGraphParams,
  'field' | 'fields'
>;

/**
 * 推移グラフの型
 */
export type PlanProgressTransitionGraph = {
  setting: PlanProgressGraphSetting;
  graphList: PlanProgressGraphFieldItem[];
};

/**
 * API 1回分のレスポンス（field 1項目あたり）
 */
export type PlanProgressGraphApiResponse = {
  setting: PlanProgressGraphParams;
  data: GraphData;
};

/**
 * API レスポンスの setting から Redux 用 setting を作る。
 */
export const toPlanProgressGraphSetting = (
  params: PlanProgressGraphParams
): PlanProgressGraphSetting => {
  const { field: _field, fields: _fields, ...setting } = params;
  return setting;
};
